import { Box, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import WifiRounded from '@mui/icons-material/WifiRounded'
import WifiOffRounded from '@mui/icons-material/WifiOffRounded'

// Online = status "good", offline = neutral: colors checked with the dataviz validator
// against the card surface in each mode (same pair as the Dashboard)
export const STATUS_COLORS = {
  dark: { online: '#43a047', offline: '#8a8fab', onlineText: '#81c784' },
  light: { online: '#2e7d32', offline: '#858585', onlineText: '#1b5e20' },
}

// The round Wi-Fi badge on its own (also used by the Online / Offline counts); online gets a soft "live" pulse
export const OnlineStatusIcon = ({ online, size = 18 }) => {
  const theme = useTheme()
  const colors = STATUS_COLORS[theme.palette.mode === 'dark' ? 'dark' : 'light']
  const color = online ? colors.online : colors.offline
  const Icon = online ? WifiRounded : WifiOffRounded

  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        color: online ? '#fff' : color,
        backgroundColor: online ? color : alpha(color, 0.2),
        ...(online && {
          animation: 'onlineChipPulse 2s ease-in-out infinite',
          '@keyframes onlineChipPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(color, 0.45)}` },
            '50%': { boxShadow: `0 0 0 4px ${alpha(color, 0)}` },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }),
      }}
    >
      <Icon sx={{ fontSize: Math.round(size * 0.67) }} />
    </Box>
  )
}

// Online / Offline pill with an icon (Users and Unit tables)
const OnlineStatusChip = ({ online, tip }) => {
  const theme = useTheme()
  const colors = STATUS_COLORS[theme.palette.mode === 'dark' ? 'dark' : 'light']
  const color = online ? colors.online : colors.offline

  const chip = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: 26,
        pl: 0.5,
        pr: 1.25,
        borderRadius: 13,
        border: `1px solid ${alpha(color, 0.4)}`,
        backgroundColor: alpha(color, online ? 0.14 : 0.1),
      }}
    >
      <OnlineStatusIcon online={online} />
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: online ? colors.onlineText : theme.palette.text.secondary,
        }}
      >
        {online ? 'Online' : 'Offline'}
      </Typography>
    </Box>
  )

  return tip ? (
    <Tooltip title={tip} placement="top" arrow>
      {chip}
    </Tooltip>
  ) : (
    chip
  )
}

export default OnlineStatusChip
